import { useEffect, useState, useRef } from 'react';
import { Dimensions } from 'react-native';
import {
  calculateDailyChartDimensions,
  calculateOptimalDisplayTime
} from './chartUtils';
import useChartAnimations from './useChartAnimations';

const useDailyChartData = ({
  response,
  processFunction,
  containerDimensions,
  metricType,
  updateAutoTime,
  chartType,
  maxBarsToShow = 6,
  barGapRatio = 0.2,
  yAxisWidth = 50,
  initialSpacing = 5,
  enableDimensionsListener = true
}) => {
  const [dataList, setDataList] = useState([]);
  const [maxValue, setMaxValue] = useState(0);
  const [loading, setLoading] = useState(!response?.data?.statistics);
  const [barWidth, setBarWidth] = useState(80);
  const [spacing, setSpacing] = useState(initialSpacing);
  const [chartWidth, setChartWidth] = useState(0);
  const [shouldScroll, setShouldScroll] = useState(false);
  const dataProcessedRef = useRef(false);

  const animations = useChartAnimations(dataList, 800, 300, 100);

  const updateChartDimensions = (data, containerDims) => {
    if (!data || data.length === 0) return;

    const dimensions = calculateDailyChartDimensions(
      data,
      containerDims,
      maxBarsToShow,
      barGapRatio,
      yAxisWidth
    );

    setBarWidth(dimensions.barWidth);
    setSpacing(dimensions.spacing);
    setChartWidth(dimensions.chartWidth);
    setShouldScroll(dimensions.shouldScroll);
  };

  const processStatistics = () => {
    if (dataProcessedRef.current) {
      return;
    }

    try {
      const result = processFunction(response, metricType);

      if (!result.success) {
        setLoading(false);
        return;
      }

      setMaxValue(result.maxValue);
      setDataList(result.dataList);
      dataProcessedRef.current = true;

      updateChartDimensions(result.dataList, containerDimensions);

      if (updateAutoTime && typeof updateAutoTime === 'function') {
        const dimensions = calculateDailyChartDimensions(
          result.dataList,
          containerDimensions,
          maxBarsToShow,
          barGapRatio,
          yAxisWidth
        );
        const optimalTime = calculateOptimalDisplayTime(
          result.dataList,
          chartType,
          maxBarsToShow,
          dimensions.barWidth + dimensions.spacing
        );
        updateAutoTime(optimalTime);
      }

      setLoading(false);
    } catch (e) {
      console.error('Error processing statistics:', e);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (response?.data?.statistics && !dataProcessedRef.current) {
      processStatistics();
    } else if (dataProcessedRef.current && dataList.length > 0) {
      updateChartDimensions(dataList, containerDimensions);
    }
  }, [response?.data?.statistics, containerDimensions, metricType, dataList]);

  useEffect(() => {
    if (metricType) {
      dataProcessedRef.current = false;
      setDataList([]);
    }
  }, [metricType]);

  useEffect(() => {
    if (!enableDimensionsListener) return;

    const updateDimensions = () => {
      if (dataList && dataList.length > 0) {
        updateChartDimensions(dataList, containerDimensions);
      }
    };

    const dimensionsHandler = Dimensions.addEventListener('change', updateDimensions);

    return () => {
      if (dimensionsHandler?.remove) {
        dimensionsHandler.remove();
      }
    };
  }, [dataList, containerDimensions, enableDimensionsListener]);

  return {
    dataList,
    maxValue,
    loading,
    barWidth,
    spacing,
    chartWidth,
    shouldScroll,
    animations
  };
};

export default useDailyChartData;
